/**
 * Soft filter chips, sort, and pagination for the collection soft filter section.
 * Owns Section Rendering fetch, pushState/popstate, skeleton, and full-page fallback.
 */
if (!customElements.get('collection-soft-filter')) {
  customElements.define(
    'collection-soft-filter',
    class CollectionSoftFilter extends HTMLElement {
      connectedCallback() {
        this.onSoftNavClick = this.handleSoftNavClick.bind(this);
        this.onSortToggleClick = this.handleSortToggleClick.bind(this);
        this.onSortKeyDown = this.handleSortKeyDown.bind(this);
        this.onDocumentPointerDown = this.handleDocumentPointerDown.bind(this);
        this.onPopState = this.handlePopState.bind(this);
        this.onAddToCartClick = this.handleAddToCartClick.bind(this);

        this.addEventListener('click', this.onSoftNavClick);
        this.addEventListener('click', this.onAddToCartClick);
        this.addEventListener('keydown', this.onSortKeyDown);

        const sortToggle = this.querySelector('[data-collection-soft-filter-sort-toggle]');
        if (sortToggle) {
          sortToggle.addEventListener('click', this.onSortToggleClick);
        }

        document.addEventListener('pointerdown', this.onDocumentPointerDown);
        window.addEventListener('popstate', this.onPopState);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onSoftNavClick);
        this.removeEventListener('click', this.onAddToCartClick);
        this.removeEventListener('keydown', this.onSortKeyDown);
        document.removeEventListener('pointerdown', this.onDocumentPointerDown);
        window.removeEventListener('popstate', this.onPopState);

        const sortToggle = this.querySelector('[data-collection-soft-filter-sort-toggle]');
        if (sortToggle) {
          sortToggle.removeEventListener('click', this.onSortToggleClick);
        }

        if (this.abortController) {
          this.abortController.abort();
          this.abortController = null;
        }
      }

      handleAddToCartClick(event) {
        const button = event.target.closest('[data-add-to-cart]');
        if (!button || !this.contains(button) || button.disabled) return;

        const form = button.closest('form[data-product-form]');
        if (!form || form.querySelector('[type="file"]')) return;

        event.preventDefault();

        const errors = form.querySelector('[data-add-action-errors]');
        button.disabled = true;
        button.classList.add('loading');

        const formData = new FormData(form);
        const body = new URLSearchParams(formData).toString();
        const cartAddUrl = `${window.theme.routes.cart}/add.js`;

        fetch(cartAddUrl, {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body,
        })
          .then((response) => {
            if (!response.ok) {
              return response.json().then((data) => Promise.reject(data));
            }
            return response.json();
          })
          .then((variant) => {
            button.classList.remove('loading');
            button.classList.add('has-success');
            setTimeout(() => {
              button.classList.remove('has-success');
              button.removeAttribute('disabled');
            }, 3500);

            const popdown = document.querySelector('[data-product-add-popdown-wrapper]');
            if (popdown) {
              popdown.dispatchEvent(
                new CustomEvent('theme:cart:popdown', {
                  detail: { variant },
                  bubbles: true,
                })
              );
            }

            return fetch(`${window.theme.routes.cart}.js`).then((response) => response.json());
          })
          .then((cart) => {
            if (!cart) return;
            document.dispatchEvent(
              new CustomEvent('theme:cart:change', {
                detail: { cart },
                bubbles: true,
              })
            );
          })
          .catch((error) => {
            button.classList.remove('loading');
            button.removeAttribute('disabled');

            const text =
              (error && (error.description || error.message)) ||
              'Network error: please try again';
            if (errors) {
              errors.innerHTML = `<div class="errors">${text}</div>`;
            }
          });
      }

      getSortPopout() {
        return this.querySelector('[data-collection-soft-filter-sort]');
      }

      getSortToggle() {
        return this.querySelector('[data-collection-soft-filter-sort-toggle]');
      }

      getSortList() {
        return this.querySelector('[data-collection-soft-filter-sort-list]');
      }

      getSortOptions() {
        return Array.from(this.querySelectorAll('[data-collection-soft-filter-sort-option]'));
      }

      isSortOpen() {
        const toggle = this.getSortToggle();
        return Boolean(toggle && toggle.getAttribute('aria-expanded') === 'true');
      }

      openSort() {
        const toggle = this.getSortToggle();
        const list = this.getSortList();
        if (!toggle || !list) return;

        toggle.setAttribute('aria-expanded', 'true');
        list.hidden = false;

        const selected =
          list.querySelector('[data-collection-soft-filter-sort-option][aria-selected="true"]') ||
          list.querySelector('[data-collection-soft-filter-sort-option]');
        if (selected) selected.focus();
      }

      closeSort({ focusToggle = false } = {}) {
        const toggle = this.getSortToggle();
        const list = this.getSortList();
        if (!toggle || !list) return;

        toggle.setAttribute('aria-expanded', 'false');
        list.hidden = true;

        if (focusToggle) toggle.focus();
      }

      handleSortToggleClick(event) {
        event.preventDefault();
        event.stopPropagation();

        if (this.isSortOpen()) {
          this.closeSort();
        } else {
          this.openSort();
        }
      }

      handleDocumentPointerDown(event) {
        if (!this.isSortOpen()) return;

        const popout = this.getSortPopout();
        if (!popout || popout.contains(event.target)) return;

        this.closeSort();
      }

      handleSortKeyDown(event) {
        const popout = this.getSortPopout();
        if (!popout || !popout.contains(event.target)) return;

        const options = this.getSortOptions();
        if (!options.length) return;

        const toggle = this.getSortToggle();
        const isToggle = event.target === toggle;
        const optionIndex = options.indexOf(event.target);

        switch (event.key) {
          case 'Escape':
            if (this.isSortOpen()) {
              event.preventDefault();
              this.closeSort({ focusToggle: true });
            }
            break;
          case 'ArrowDown':
            event.preventDefault();
            if (!this.isSortOpen()) {
              this.openSort();
              break;
            }
            if (isToggle) {
              options[0].focus();
            } else if (optionIndex > -1) {
              options[(optionIndex + 1) % options.length].focus();
            }
            break;
          case 'ArrowUp':
            event.preventDefault();
            if (!this.isSortOpen()) {
              this.openSort();
              break;
            }
            if (isToggle) {
              options[options.length - 1].focus();
            } else if (optionIndex > -1) {
              options[(optionIndex - 1 + options.length) % options.length].focus();
            }
            break;
          case 'Home':
            if (this.isSortOpen() && optionIndex > -1) {
              event.preventDefault();
              options[0].focus();
            }
            break;
          case 'End':
            if (this.isSortOpen() && optionIndex > -1) {
              event.preventDefault();
              options[options.length - 1].focus();
            }
            break;
          case 'Tab':
            if (this.isSortOpen()) {
              this.closeSort();
            }
            break;
          default:
            break;
        }
      }

      handleSoftNavClick(event) {
        const link = event.target.closest(
          [
            '[data-collection-soft-filter-chips] a[href]',
            '[data-collection-soft-filter-clear][href]',
            '[data-collection-soft-filter-pagination] a[href]',
            '[data-collection-soft-filter-sort-option][href]',
          ].join(', ')
        );
        if (!link || !this.contains(link)) return;

        event.preventDefault();
        this.closeSort();
        this.softNavigate(link.href, { pushState: true });
      }

      handlePopState() {
        this.softNavigate(window.location.pathname + window.location.search, { pushState: false });
      }

      softNavigate(url, { pushState }) {
        if (this.abortController) {
          this.abortController.abort();
        }
        this.abortController = new AbortController();

        const sectionId = this.dataset.sectionId;
        const sectionRoot = this.closest('.shopify-section');
        if (!sectionId || !sectionRoot) {
          window.location.assign(url);
          return;
        }

        this.showSkeleton();

        const fetchUrl = new URL(url, window.location.origin);
        fetchUrl.searchParams.set('section_id', sectionId);

        fetch(fetchUrl.toString(), { signal: this.abortController.signal })
          .then((response) => {
            if (!response.ok) throw new Error('Section fetch failed');
            return response.text();
          })
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const newSection = doc.getElementById(`shopify-section-${sectionId}`);
            if (!newSection) throw new Error('Section missing from response');

            sectionRoot.replaceWith(newSection);

            if (pushState) {
              const shareUrl = new URL(url, window.location.origin);
              shareUrl.searchParams.delete('section_id');
              const next =
                shareUrl.pathname + shareUrl.search + (shareUrl.hash || '');
              history.pushState({}, '', next);
            }

            const updated = document.getElementById(`shopify-section-${sectionId}`);
            if (updated) {
              updated.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          })
          .catch((error) => {
            if (error && error.name === 'AbortError') return;
            window.location.assign(url);
          });
      }

      showSkeleton() {
        this.setAttribute('aria-busy', 'true');

        const sortToggle = this.getSortToggle();
        if (sortToggle) sortToggle.disabled = true;

        const products = this.querySelector('[data-collection-soft-filter-products]');
        if (!products) return;

        const grid = products.querySelector('[data-grid-large]');
        const columnsLarge = grid
          ? parseInt(grid.getAttribute('data-grid-large'), 10) || 3
          : 3;
        const columnsSmall = grid
          ? parseInt(grid.getAttribute('data-grid-small'), 10) || 2
          : 2;
        const count = Math.max(columnsLarge * 2, 6);

        let cells = '';
        for (let i = 0; i < count; i++) {
          cells += '<div class="collection-soft-filter__skeleton-card" aria-hidden="true"></div>';
        }

        products.innerHTML =
          `<div class="collection-soft-filter__skeleton js-grid" data-grid-large="${columnsLarge}" data-grid-small="${columnsSmall}" role="status">${cells}</div>`;
      }
    }
  );
}
